import { lazy, type ComponentType } from "react";

// 페이지 청크(코드 스플리팅 동적 import) 로딩 실패를 자동 복구하는 lazy 래퍼.
//
// 왜 필요한가:
// 새 배포가 나가면 청크 파일명에 박힌 해시가 바뀐다(PolicyRebateMatrixPage-AAA.js → -BBB.js).
// 배포 "전"부터 탭을 열어둔 사용자의 브라우저는 머릿속에 옛 파일명을 들고 있다가,
// 그 페이지로 이동하는 순간 이미 사라진 옛 파일을 받으러 가서 실패한다
// → "Failed to fetch dynamically imported module". 코드 버그가 아니라 배포 타이밍 문제다.
//
// 어떻게 고치나:
// 이 실패는 십중팔구 "그 사이 새 배포가 나갔다"는 신호이므로, 페이지를 딱 한 번 새로고침해
// 최신 index.html(= 새 청크 목록)을 받아오면 그대로 풀린다. 사용자는 잠깐의 새로고침만 겪고
// 에러 화면(ErrorBoundary)은 보지 않는다.
//
// 무한 새로고침 방지:
// sessionStorage에 "이미 한 번 새로고침했다" 도장을 찍는다. 새로고침 후에도 또 실패하면
// (= 진짜 네트워크 장애나 실제 404 등) 더는 reload하지 않고 에러를 그대로 던져 ErrorBoundary가
// 받게 한다. 로딩에 성공하면 도장을 지워, 다음 배포 때 자동복구가 다시 작동하도록 한다.
const RELOAD_GUARD_KEY = "chunk-reload-attempted";

// 이 페이지 수명 안에서 새로고침을 이미 시작했는가.
// sessionStorage 가드(RELOAD_GUARD_KEY)는 "새로고침을 겪은 뒤에도 또 실패하는가"를 세는 것이라
// 새로고침 **직후 페이지**에도 도장이 남아 있다. 반면 이 변수는 "지금 이 순간 브라우저가
// 페이지를 갈아엎는 중"이라는 훨씬 좁은 사실을 가리킨다. 둘을 구분해야, 갈아엎히기 직전의
// 찰나에 에러 화면을 띄우는 일을 막을 수 있다(아래 lazyWithRetry).
let reloadInFlight = false;

/** 로딩에 성공했으니 다음 배포 때 자동복구가 다시 가능하도록 가드를 해제한다. */
function clearGuard(): void {
  try {
    sessionStorage.removeItem(RELOAD_GUARD_KEY);
  } catch {
    /* sessionStorage 접근 불가(시크릿 모드 등)면 무시 */
  }
}

/**
 * "새 배포가 나갔다"고 보고 딱 한 번 새로고침한다.
 *
 * 페이지 청크 말고 **여러 페이지가 나눠 쓰는 공용 청크**도 같은 이유로 404 가 날 수 있는데,
 * 그건 lazyWithRetry 까지 도달하지 못하고 vite 가 `vite:preloadError` 로 따로 알려 준다.
 * 그쪽에서도 같은 처방을 쓰도록 꺼내 둔다 — **가드 도장을 공유해야** 두 층이 각자 세어
 * 두 번 새로고침하는 일이 없다(main.tsx).
 *
 * @returns 새로고침을 시작했으면 true. 이미 한 번 했으면 false(= 호출자가 에러를 던져야 한다).
 */
export function reloadOnceForNewBuild(): boolean {
  let alreadyReloaded = false;
  try {
    alreadyReloaded = sessionStorage.getItem(RELOAD_GUARD_KEY) === "1";
    if (!alreadyReloaded) sessionStorage.setItem(RELOAD_GUARD_KEY, "1");
  } catch {
    /* sessionStorage 못 쓰면 가드 없이 1회 reload 시도 */
  }
  if (alreadyReloaded) return false;
  // 새 빌드(index.html + 새 청크)를 받아오기 위해 페이지를 새로고침한다.
  reloadInFlight = true;
  window.location.reload();
  return true;
}

/** 새로고침을 이미 시작했는가(= 곧 이 페이지는 통째로 사라진다). */
export function isReloadInFlight(): boolean {
  return reloadInFlight;
}

// React 원본 lazy 와 같은 제약: 모든 페이지 컴포넌트(제각각의 props)를 받아야 하므로
// any 가 맞다(unknown 으로 좁히면 props 있는 컴포넌트가 할당 거부됨).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const mod = await factory();
      // ⚠️ 실패가 "성공"으로 둔갑해 들어오는 경로가 하나 있다(2026-09-03 운영 사고).
      // vite 의 청크 로더는 실패하면 `vite:preloadError` 를 쏘고, **아무도 preventDefault 하지
      // 않았을 때만** 에러를 다시 던진다(`baseModule().catch(handlePreloadError)`).
      // main.tsx 가 그 이벤트를 받아 새로고침을 걸며 preventDefault 하므로, 여기서는 에러가
      // 아니라 **값이 undefined 인 정상 완료**로 돌아온다 → 아래 catch 로 오지 않는다.
      // 그대로 React 에 넘기면 React 가 undefined 에서 default 를 꺼내려다 터진다
      // ("Cannot read properties of undefined (reading 'default')").
      if (!mod) {
        if (isReloadInFlight() || reloadOnceForNewBuild()) {
          // 곧 페이지가 새로 뜬다 — 그 찰나에 에러 화면이 깜빡이지 않도록 영원히 기다린다.
          return new Promise<{ default: T }>(() => {});
        }
        // 화면(ErrorBoundary)에 뜨는 문구 — 영어 꼬리표 없이 할 일을 말한다.
        throw new Error("새 버전이 나와 화면을 다시 불러와야 해요. 새로고침해 주세요.");
      }
      clearGuard();
      return mod;
    } catch (err) {
      if (isReloadInFlight() || reloadOnceForNewBuild()) {
        // reload가 진행되는 동안 영원히 멈춘 Promise를 반환해, 그 찰나에 에러 화면이
        // 깜빡 뜨는 것을 막는다(곧 페이지가 통째로 새로 뜬다).
        return new Promise<{ default: T }>(() => {});
      }
      // 새로고침까지 했는데도 실패 → 진짜 문제. ErrorBoundary로 넘긴다.
      throw err;
    }
  });
}
